import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Chatbot.css";

/* MUI */
import {
  Paper,
  IconButton,
  TextField,
  Chip,
  Avatar,
  CircularProgress,
  Tooltip,
} from "@mui/material";

/* MUI Icons */
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import MosqueRoundedIcon from "@mui/icons-material/MosqueRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

/* axios instance */
import backend from "../../../../../api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUserData } from "../../../../../redux-store/auth/actions";

/**
 * Server proxies OpenAI + tool calls
 */
const CHAT_API = "/api/chat";

const SUGGESTIONS = [
  { text: "Login with email test@site.com pass 123456", icon: <HelpOutlineRoundedIcon /> },
  { text: "Sign up with name Ali, email ali@site.com, pass 123456", icon: <HelpOutlineRoundedIcon /> },
  { text: "Create community post: Bismillah — starting my Umrah!", icon: <MosqueRoundedIcon /> },
  { text: "Add members to my group", icon: <MapRoundedIcon /> },
];

const SYSTEM_PROMPT =
  "You are Tawaf Assistant. Keep it short and action-oriented. " +
  "If the user provides login/signup info, call the appropriate tool. " +
  "For navigation or app actions, prefer tools. " +
  "Do not reveal secrets.";

function Chatbot() {
  const [messages, setMessages] = useState([
    {
      id: "hello",
      role: "assistant",
      content: "Assalamu alaikum! I’m your Tawaf Assistant. How can I help today?",
    },
  ]);
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const listRef = useRef(null);
  const abortRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const stopRequest = () => {
    abortRef.current?.abort?.();
    setLoading(false);
    setPendingId(null);
  };

  const pushAssistant = (id, text) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: text } : m)));
  };

  const smoothReveal = async (id, full, update) => {
    if (!full) return update(id, "");
    const parts = splitSoft(full, 3);
    let out = "";
    for (const p of parts) {
      out += p;
      update(id, out);
      await new Promise((r) => setTimeout(r, 140));
    }
  };

  const splitSoft = (text, chunks = 3) => {
    if (text.length < 60) return [text];
    const size = Math.ceil(text.length / chunks);
    const arr = [];
    for (let i = 0; i < text.length; i += size) arr.push(text.slice(i, i + size));
    return arr;
  };

  // ---- Apply server-side effects returned with the response (authoritative) ----
  const applyEffects = (effects) => {
    if (!Array.isArray(effects)) return;

    for (const eff of effects) {
      if (!eff || !eff.type) continue;

      if (eff.type === "dispatch" && eff.action) {
        // Currently we only need to handle auth/setUserData and auth/logout
        if (eff.action === "auth/setUserData" && eff.payload) {
          dispatch(setUserData(eff.payload));
          try {
            localStorage.setItem(
              "user",
              JSON.stringify({
                _id: eff.payload._id,
                email: eff.payload.email,
                fullname: eff.payload.fullname || eff.payload.name || "",
                mobileNumber: eff.payload.mobileNumber || eff.payload.phone || "",
                role: eff.payload.role,
                status: eff.payload.status,
                createdAt: eff.payload.createdAt,
                updatedAt: eff.payload.updatedAt,
                token: eff.payload.token,
                profileExists: eff.payload.profileExists,
              })
            );
          } catch {}
          continue;
        }
        if (eff.action === "auth/logout") {
          try { localStorage.removeItem("user"); } catch {}
          // If you have an auth.logout() action creator you can dispatch it here as well
          continue;
        }
      }

      if (eff.type === "navigate" && eff.path) {
        navigate(eff.path);
        continue;
      }

      if (eff.type === "openModal" && eff.id) {
        window.dispatchEvent(new CustomEvent("open-modal", { detail: eff.id }));
        continue;
      }
    }
  };

  const send = async (quickText) => {
    const content = (quickText ?? input).trim();
    if (!content) return;

    const userMsg = { id: crypto.randomUUID(), role: "user", content };
    const asstId = crypto.randomUUID();

    setMessages((prev) => [...prev, userMsg, { id: asstId, role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);
    setPendingId(asstId);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const payload = {
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          ...messages.map(({ role, content }) => ({ role, content })),
          { role: "user", content },
        ],
        session: user?._id
          ? {
              userId: String(user._id),
              role: user?.role || "customer",
              profile: {
                name: user?.name || user?.fullname,
                email: user?.email,
                phone: user?.phone || user?.mobileNumber,
                language: user?.language,
                subscription: user?.subscription,
                status: user?.status,
              },
            }
          : null,
      };

      const res = await backend.post(CHAT_API, payload, {
        signal: controller.signal,
        responseType: "json",
      });

      const text = res?.data?.content || "Done.";
      const effects = res?.data?.effects || [];
      // Apply effects FIRST so Redux/localStorage update immediately
      applyEffects(effects);
      await smoothReveal(asstId, text, pushAssistant);
    } catch (_err) {
      pushAssistant(pendingId || "tmp", "Sorry, I couldn’t complete that. Please try again.");
    } finally {
      setLoading(false);
      setPendingId(null);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) send();
    }
  };

  return (
    <div className="twf-shell">
      <div className="twf-chat">
        {/* Header */}
        <Paper className="twf-head" elevation={0}>
          <div className="twf-head__brand">
            <div className="twf-logo">Tawaf</div>
            <span className="twf-title">Assistant</span>
          </div>
          <div className="twf-head__status">
            <span className="twf-dot" />
            Online
          </div>
        </Paper>

        {/* Conversation */}
        <div className="twf-body" ref={listRef} aria-live="polite">
          {messages.map((m) => (
            <div key={m.id} className={`twf-msg ${m.role === "user" ? "is-user" : "is-bot"}`}>
              <Avatar
                className="twf-msg__avatar"
                src={m.role === "user" && user?.imageURL ? user.imageURL : undefined}
                alt={m.role === "user" ? (user?.name || "You") : "Assistant"}
                sx={{ bgcolor: m.role === "user" ? (user?.imageURL ? undefined : "#e0e0e0") : "#FCAA30" }}
              >
                {m.role === "user" ? "You" : <MosqueRoundedIcon />}
              </Avatar>

              <div className="twf-bubble">
                {m.content ? (
                  <div className="twf-markdown">{m.content}</div>
                ) : (
                  <div className="twf-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                )}
              </div>
            </div>
          ))}

          {messages.length <= 2 && (
            <div className="twf-suggest">
              {SUGGESTIONS.map((s) => (
                <Chip key={s.text} className="twf-chip" icon={s.icon} label={s.text} onClick={() => send(s.text)} variant="outlined" />
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <Paper className="twf-input" elevation={0}>
          <TextField
            className="twf-field"
            placeholder='Try: “login with email X and password Y”, “sign me up…”, “create a community post…”, “open settings”'
            multiline
            maxRows={6}
            value={input}
            style={{ width: "100%" }}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
          />
          {loading ? (
            <Tooltip title="Stop">
              <span>
                <IconButton className="twf-stop" onClick={stopRequest} size="large">
                  <StopCircleRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Send">
              <span>
                <IconButton className="twf-send" onClick={() => send()} disabled={!canSend} size="large">
                  <SendRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Paper>

        {loading && (
          <div className="twf-loading">
            <CircularProgress size={22} />
            <span>Preparing a helpful answer…</span>
            <BoltRoundedIcon className="twf-bolt" />
          </div>
        )}
      </div>
    </div>
  );
}

export default Chatbot;
